import type * as Monaco from 'monaco-editor';

/**
 * Registers a "liquid" language in Monaco with:
 *   - Liquid tag / output highlighting
 *   - Embedded HTML fallback for everything outside {% %} / {{ }}
 *   - Basic auto-closing of {%, {{, and HTML tags
 *
 * Called once after Monaco boots.
 */
export function registerLiquidLanguage(monaco: typeof Monaco) {
  // Already registered → noop
  if (monaco.languages.getLanguages().some((l) => l.id === 'liquid')) return;

  monaco.languages.register({
    id: 'liquid',
    extensions: ['.liquid'],
    aliases: ['Liquid', 'liquid'],
  });

  monaco.languages.setLanguageConfiguration('liquid', {
    comments: { blockComment: ['{% comment %}', '{% endcomment %}'] },
    brackets: [
      ['<', '>'],
      ['{%', '%}'],
      ['{{', '}}'],
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{%', close: ' %}' },
      { open: '{{', close: ' }}' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: '<', close: '>' },
    ],
    surroundingPairs: [
      { open: '{%', close: '%}' },
      { open: '{{', close: '}}' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  });

  const liquidKeywords = [
    'if','elsif','else','endif','unless','endunless','case','when','endcase',
    'for','endfor','break','continue','assign','capture','endcapture','increment',
    'decrement','include','render','section','layout','schema','endschema',
    'comment','endcomment','raw','endraw','liquid','echo','form','endform',
    'paginate','endpaginate','tablerow','endtablerow','style','endstyle',
    'javascript','endjavascript','stylesheet','endstylesheet',
  ];

  const liquidOperators = [
    'and','or','not','contains','in','true','false','nil','blank','empty','with',
    'by','as',
  ];

  monaco.languages.setMonarchTokensProvider('liquid', {
    defaultToken: '',
    tokenPostfix: '.liquid',
    keywords: liquidKeywords,
    operators: liquidOperators,
    tokenizer: {
      root: [
        // Liquid comment block
        [/\{%-?\s*comment\s*-?%\}/, { token: 'comment.liquid', next: '@liquidComment' }],
        // Liquid schema block (JSON inside)
        [/\{%-?\s*schema\s*-?%\}/, { token: 'keyword.tag.liquid', next: '@liquidSchema' }],
        // Liquid raw block (pass-through)
        [/\{%-?\s*raw\s*-?%\}/, { token: 'keyword.tag.liquid', next: '@liquidRaw' }],
        // Tag
        [/\{%-?/, { token: 'delimiter.liquid', next: '@liquidTag' }],
        // Output
        [/\{\{-?/, { token: 'delimiter.liquid', next: '@liquidOutput' }],

        // HTML fallthrough (very lightweight)
        [/<!--/, { token: 'comment.html', next: '@htmlComment' }],
        [/<!DOCTYPE/, { token: 'metatag.html', next: '@doctype' }],
        [/<\/?[a-zA-Z][\w-]*/, { token: 'tag.html', next: '@htmlTag' }],
        [/&[a-z]+;/, 'string.html'],
        [/[<>&]/, 'delimiter.html'],
        [/[^<{&]+/, ''],
      ],

      liquidComment: [
        [/\{%-?\s*endcomment\s*-?%\}/, { token: 'comment.liquid', next: '@pop' }],
        [/./, 'comment.liquid'],
      ],

      liquidSchema: [
        [/\{%-?\s*endschema\s*-?%\}/, { token: 'keyword.tag.liquid', next: '@pop' }],
        // treat the inside as "json-ish" strings/numbers
        [/"[^"\\]*(?:\\.[^"\\]*)*"/, 'string.json'],
        [/\b\d+(\.\d+)?\b/, 'number.json'],
        [/\b(true|false|null)\b/, 'keyword.json'],
        [/[{}[\],:]/, 'delimiter.json'],
        [/./, 'string.json'],
      ],

      liquidRaw: [
        [/\{%-?\s*endraw\s*-?%\}/, { token: 'keyword.tag.liquid', next: '@pop' }],
        [/./, ''],
      ],

      liquidTag: [
        [/-?%\}/, { token: 'delimiter.liquid', next: '@pop' }],
        [/"[^"]*"/, 'string.liquid'],
        [/'[^']*'/, 'string.liquid'],
        [/\b\d+(\.\d+)?\b/, 'number.liquid'],
        [
          /[a-zA-Z_][\w]*/,
          {
            cases: {
              '@keywords': 'keyword.tag.liquid',
              '@operators': 'operator.liquid',
              '@default': 'identifier.liquid',
            },
          },
        ],
        [/[|:.,=<>!]+/, 'operator.liquid'],
        [/\s+/, ''],
      ],

      liquidOutput: [
        [/-?\}\}/, { token: 'delimiter.liquid', next: '@pop' }],
        [/"[^"]*"/, 'string.liquid'],
        [/'[^']*'/, 'string.liquid'],
        [/\b\d+(\.\d+)?\b/, 'number.liquid'],
        [
          /[a-zA-Z_][\w]*/,
          {
            cases: {
              '@operators': 'operator.liquid',
              '@default': 'identifier.liquid',
            },
          },
        ],
        [/[|:.,]+/, 'operator.liquid'],
        [/\s+/, ''],
      ],

      htmlComment: [
        [/-->/, { token: 'comment.html', next: '@pop' }],
        [/./, 'comment.html'],
      ],

      doctype: [
        [/[^>]+/, 'metatag.content.html'],
        [/>/, { token: 'metatag.html', next: '@pop' }],
      ],

      htmlTag: [
        [/\{\{-?/, { token: 'delimiter.liquid', next: '@liquidOutput' }],
        [/\{%-?/, { token: 'delimiter.liquid', next: '@liquidTag' }],
        [/[a-zA-Z_:][\w\-:.]*/, 'attribute.name.html'],
        [/=/, 'delimiter.html'],
        [/"[^"]*"/, 'attribute.value.html'],
        [/'[^']*'/, 'attribute.value.html'],
        [/\/?>/, { token: 'tag.html', next: '@pop' }],
        [/\s+/, ''],
      ],
    },
  });

  // Softer theme tweak — ensure liquid tokens read well on dark/light
  monaco.editor.defineTheme('lc-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'delimiter.liquid', foreground: '7c3aed', fontStyle: 'bold' },
      { token: 'keyword.tag.liquid', foreground: '005bd3', fontStyle: 'bold' },
      { token: 'identifier.liquid', foreground: '1a1a1a' },
      { token: 'operator.liquid', foreground: '616161' },
      { token: 'string.liquid', foreground: '008060' },
      { token: 'number.liquid', foreground: 'b98900' },
      { token: 'comment.liquid', foreground: '8c9196', fontStyle: 'italic' },
    ],
    colors: {
      'editor.background': '#ffffff',
      'editor.foreground': '#1a1a1a',
      'editorLineNumber.foreground': '#8c9196',
      'editorLineNumber.activeForeground': '#1a1a1a',
      'editor.selectionBackground': '#cfe4ff',
      'editor.inactiveSelectionBackground': '#ebf5ff',
      'editorCursor.foreground': '#005bd3',
      'editor.lineHighlightBackground': '#f6f6f7',
      'editorIndentGuide.background': '#ebebeb',
      'editorWhitespace.foreground': '#e1e3e5',
    },
  });
}

/**
 * Pick the Monaco language id for a given path.
 */
export function languageForPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'liquid': return 'liquid';
    case 'json': return 'json';
    case 'js':
    case 'mjs': return 'javascript';
    case 'ts': return 'typescript';
    case 'css': return 'css';
    case 'scss': return 'scss';
    case 'html': return 'html';
    case 'md': return 'markdown';
    case 'svg':
    case 'xml': return 'xml';
    default: return 'plaintext';
  }
}
